import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  X, ChevronLeft, ChevronRight, Trophy, LayoutDashboard, ListOrdered, Target,
  Shield, GitBranch, Star, HelpCircle, BarChart3, MessageSquare, Users, Rocket, ArrowRight,
} from "lucide-react";

const ALL_STEPS = [
  {
    id: "welcome", icon: Trophy, tag: "Welcome", title: "Welcome to Jinxing Sports",
    body: "Your prediction battleground for the tournament. Compete across up to five game modes, climb live leaderboards, talk trash in the chat, and prove you called it.",
    bullets: ["Predict match scores for points", "Survive, build brackets & fantasy XIs", "Climb individual, group & mode boards"],
  },
  {
    id: "dashboard", icon: LayoutDashboard, tag: "Dashboard", title: "Your Home Base", to: "/dashboard",
    body: "Everything at a glance — your points, streaks and per-competition status.",
    bullets: [
      "Stat tiles: points, correct results, exact scores & streaks",
      "Status chips show what you still need to enter for the next matchday",
      "Catch the Upset of the Week",
      "Spotted a bug or have an idea? Hit the Report button (message icon, top bar) anytime",
    ],
  },
  {
    id: "standings", icon: ListOrdered, tag: "Standings", title: "Live Group Tables", to: "/standings",
    body: "Real group standings, computed from settled results — not guesswork.",
    bullets: ["Win = 3, Draw = 1", "Ranked by points, then goal difference, then goals for", "Updates the instant a match is settled"],
  },
  {
    id: "predict", comp: "main_league", icon: Target, tag: "Predict", title: "Main League — Call the Scores", to: "/predict",
    body: "Predict the exact scoreline of every match. Scoring is tiered (not stacked):",
    bullets: [
      "Exact score = 5 · Goal difference = 3 · Correct result = 1",
      "Picks save automatically as you type — no Save button. Each match locks at its own kickoff",
      "Spend your Captain (2×) and Power Play once per matchday, and grab Underdog bonuses for calling upsets",
    ],
  },
  {
    id: "survivor", comp: "survivor", icon: Shield, tag: "Survivor", title: "Survivor Challenge", to: "/survivor",
    body: "Pick teams to win each matchday and stay alive.",
    bullets: [
      "Pick the required number of teams every matchday (one per match)",
      "A team that draws or loses is a strike — collect too many and you're out",
      "Never reuse a team. Last players standing win the crown",
    ],
  },
  {
    id: "bracket", comp: "bracket", icon: GitBranch, tag: "Bracket", title: "Bracket Cascade", to: "/bracket",
    body: "Map out the whole tournament before it kicks off.",
    bullets: [
      "Rank each group 1st–4th and pick the best third-placed teams",
      "Choose knockout winners all the way to your champion",
      "Use Save & Next — your bracket locks at the first kickoff",
    ],
  },
  {
    id: "fantasy", comp: "fantasy", icon: Star, tag: "Fantasy XI", title: "Build Your Fantasy XI", to: "/fantasy",
    body: "Draft a starting eleven within budget and earn from their real performances.",
    bullets: ["Stay under the budget cap", "Respect the max-players-per-nation limit", "Name a captain for bonus points"],
  },
  {
    id: "bonus", comp: "bonus_questions", icon: HelpCircle, tag: "Bonus", title: "Bonus Questions", to: "/bonus",
    body: "Answer tournament prop questions for extra points — top scorer, finalists, surprise packages and more.",
    bullets: ["Lock in your answers before they close", "Every correct call adds to your total"],
  },
  {
    id: "leaderboard", icon: BarChart3, tag: "Leaderboard", title: "Where You Rank", to: "/leaderboard",
    body: "Four boards, one page — switch tabs to see how you stack up.",
    bullets: [
      "Individual — the main overall board",
      "Bracket & Survivor — mode-specific standings",
      "Groups — ranked by average score",
    ],
  },
  {
    id: "chat", icon: MessageSquare, tag: "Chat", title: "Talk Trash, Share Calls", to: "/chat",
    body: "Reddit-style rooms for the tournament and your groups.",
    bullets: ["Post, reply and upvote the hottest takes", "@mention rivals to call them out", "Auto-posts highlight upsets and big moments"],
  },
  {
    id: "groups", icon: Users, tag: "Groups", title: "Join the Squad", to: "/profile",
    body: "Represent a group and battle for the group crown.",
    bullets: ["Join from your Profile or via an invite link", "Group standings live on the Leaderboard's Groups tab", "Group admins can manage members and invites"],
  },
  {
    id: "go", icon: Rocket, tag: "You're Ready", title: "Time to Make Your Picks",
    body: "That's the full tour. Head to the Predict page and lock in your first matchday — points are waiting.",
    bullets: ["Found a bug or want a feature? Use the Report button (message icon in the top bar) — we read every one"],
    cta: { label: "Make Predictions", to: "/predict" },
  },
];

export default function Walkthrough({ open, onClose, competitions = {} }) {
  const navigate = useNavigate();
  const [i, setI] = useState(0);

  const steps = useMemo(
    () => ALL_STEPS.filter((s) => !s.comp || competitions[s.comp]),
    [competitions]
  );

  const finish = useCallback(() => { setI(0); onClose?.(); }, [onClose]);
  const next = () => setI((v) => Math.min(steps.length - 1, v + 1));
  const back = () => setI((v) => Math.max(0, v - 1));

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, finish, steps.length]);

  if (!open) return null;
  const step = steps[i];
  const Icon = step.icon;
  const isLast = i === steps.length - 1;

  const goPage = () => { finish(); if (step.to) navigate(step.to); };
  const doCta = () => { finish(); if (step.cta?.to) navigate(step.cta.to); };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="walkthrough-modal" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={finish} />
      <div className="relative w-full max-w-lg surface-card border border-white/10 overflow-hidden animate-[fadeIn_0.2s_ease]">
        {/* accent header strip */}
        <div className="h-1 bg-primary-accent" />
        <button
          data-testid="walkthrough-skip"
          onClick={finish}
          className="absolute top-3 right-3 flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white"
        >
          Skip <X size={14} />
        </button>

        <div key={step.id} className="p-7 md:p-9 animate-[fadeIn_0.25s_ease]">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-primary-accent flex items-center justify-center flex-shrink-0">
              <Icon size={22} className="text-white" />
            </div>
            <div>
              <div className="label-tag text-primary-accent">{step.tag}</div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">Step {i + 1} of {steps.length}</div>
            </div>
          </div>

          <h2 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-3" data-testid="walkthrough-title">{step.title}</h2>
          <p className="text-sm md:text-base text-zinc-300 leading-relaxed">{step.body}</p>

          {step.bullets?.length > 0 && (
            <ul className="mt-4 space-y-2">
              {step.bullets.map((b, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-zinc-400">
                  <ArrowRight size={14} className="text-primary-accent mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {step.to && !isLast && (
            <button data-testid="walkthrough-open-page" onClick={goPage}
              className="mt-5 text-xs uppercase tracking-widest text-primary-accent hover:text-white inline-flex items-center gap-1">
              Open {step.tag} <ArrowRight size={12} />
            </button>
          )}
          {step.cta && (
            <button data-testid="walkthrough-cta" onClick={doCta}
              className="mt-6 w-full bg-primary-accent text-white px-5 py-3 font-display tracking-widest text-sm uppercase flex items-center justify-center gap-2 hover:bg-[var(--primary-hover)]">
              {step.cta.label} <ArrowRight size={16} />
            </button>
          )}
        </div>

        {/* footer nav */}
        <div className="flex items-center justify-between gap-3 px-7 md:px-9 py-4 border-t border-white/5">
          <button
            data-testid="walkthrough-back"
            onClick={back}
            disabled={i === 0}
            className="flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-white disabled:opacity-30"
          >
            <ChevronLeft size={14} /> Back
          </button>

          <div className="flex items-center gap-1.5">
            {steps.map((s, idx) => (
              <button
                key={s.id}
                aria-label={`Go to step ${idx + 1}`}
                onClick={() => setI(idx)}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-primary-accent" : "w-1.5 bg-zinc-700 hover:bg-zinc-500"}`}
              />
            ))}
          </div>

          {isLast ? (
            <button data-testid="walkthrough-finish" onClick={finish}
              className="flex items-center gap-1 text-xs uppercase tracking-widest text-primary-accent hover:text-white">
              Done
            </button>
          ) : (
            <button data-testid="walkthrough-next" onClick={next}
              className="flex items-center gap-1 text-xs uppercase tracking-widest text-primary-accent hover:text-white">
              Next <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
